import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { useFacility } from "hooks/useFacility";
import UpgradePage from "pages/dashboard/UpgradePage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import DashboardLayout from "components/dashboard/DashboardLayout";
import Settings from "components/dashboard/Settings";
import PatientOnboardingDialog from "components/dashboard/PatientOnboardingDialog";
import HealthRecordsHub from "components/dashboard/HealthRecordsHub";
import MasterChartSetupPage from "components/dashboard/healthRecords/masterChartSetup";
import PatientHomePage from "pages/dashboard/individual/PatientHomePage";
import Loading from "components/Loading";
// Static, unlike the page itself: this provider holds an in-flight check-in for
// the whole patient dashboard, so it must not arrive with the lazy chunk.
import { VisitStartDraftProvider } from "features/visitStart/visitStartDraftStore";
// Lazy so the voice engine + audio bundle stays out of the patient chunk until CareStart opens.
const CareStartPage = React.lazy(() => import("features/careStart/CareStartPage"));
// Lazy for the same reason as the others: it pulls in the record review and the
// LLM client, neither of which the rest of the patient dashboard needs.
const AssistantPage = React.lazy(() => import("features/assistant/AssistantPage"));
// Lazy for the same reason: pulls in react-hook-form and the element catalog.
const VisitStartPage = React.lazy(() => import("features/visitStart/VisitStartPage"));
import DemographicsPage from "pages/dashboard/individual/health-records/DemographicsPage";
import MedicalConditionsPage from "pages/dashboard/individual/health-records/MedicalConditionsPage";
import SurgicalHistoryPage from "pages/dashboard/individual/health-records/SurgicalHistoryPage";
import AllergiesPage from "pages/dashboard/individual/health-records/AllergiesPage";
import OtherHistoryPage from "pages/dashboard/individual/health-records/OtherHistoryPage";
import MedicationsPage from "pages/dashboard/individual/MedicationsPage";
// TODO: Messages (facility <> patient comms) disabled for beta launch — beta users have no facility-role access yet
// import MessagesPage from "pages/dashboard/individual/MessagesPage";
import DischargeInstructionsPage from "pages/dashboard/individual/DischargeInstructionsPage";
// TODO: MedicationTrackingPage disabled until Twilio BAA is in place (no reminders = no tracking utility)
// import MedicationTrackingPage from "pages/dashboard/individual/MedicationTrackingPage";
import { db } from "services/firebase";
import { masterChartLegacyRedirectTarget } from "constants/masterChartRoutes";
import { isDevEnvironment } from "utils/isDevEnvironment";
import { PromptsManagerProvider } from "context/PromptsManager";
import ChatPage from "components/chat_new/ChatPage";

function MasterChartLegacyRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={masterChartLegacyRedirectTarget(pathname, search)} replace />;
}

const PatientRoutes = () => {
  const { user, userData, subscription, isGlobalAdmin } = useAuth();
  const { data } = useFacility();
  const location = useLocation();
  const [showPatientOnboarding, setShowPatientOnboarding] = useState(false);

  // Routes that fill the content area instead of the narrow page column: the Medical Library chat
  // (its wide composer can't sit in a narrow column), CareStart, and VisitStart (both own their
  // whole screen via StepperPageShell, which already supplies the height, padding and scroll).
  // All keep the dashboard sidebar — they just drop the centered Container. The home hub at
  // /dashboard uses the normal centered column.
  //
  // VisitStart was missing from this list, so its shell was nested inside
  // DashboardLayout's centered 1024px Container while ChartMind and CareStart sat
  // edge to edge — the same page frame rendering at two different widths.
  const FULL_BLEED_SEGMENTS = new Set(["assistant", "carestart", "medical-library", "visitstart"]);
  const isFullBleedRoute = () => {
    const segments = location.pathname.replace(/\/$/, "").split("/").filter(Boolean);
    if (segments[0] !== "dashboard") return false;
    return FULL_BLEED_SEGMENTS.has(segments[1]);
  };

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (userData?.role === "patient" && !userData?.hideOnboardingDialog) {
            setShowPatientOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  return (
    /**
     * An in-flight VisitStart check-in lives here, the same way
     * ChartMindSessionProvider holds an in-progress encounter: the check-in links
     * out to the MasterChart, and a patient who follows that link has to come
     * back to the question they left rather than to the opening screen.
     *
     * ABOVE DashboardLayout, which is not a detail. The layout renders `children`
     * in two different places — bare when `fullWidth`, inside a `Container`
     * otherwise — so moving between a full-bleed route (/dashboard/visitstart) and
     * a normal one (/dashboard/medications) swaps the element type at that
     * position and React unmounts the whole subtree. Anything held below the
     * layout is destroyed by that navigation, which is exactly the trip this
     * provider exists to survive.
     *
     * RAM only, so closing the tab still ends the check-in.
     */
    <VisitStartDraftProvider>
      <DashboardLayout fullWidth={isFullBleedRoute()}>
        <PatientOnboardingDialog
          open={showPatientOnboarding}
          onClose={() => setShowPatientOnboarding(false)}
          userId={user?.uid}
        />

        <PromptsManagerProvider>
          <Routes>
            <Route path="/" element={<PatientHomePage />} />

            <Route
              path="/medical-library"
              element={<ChatPage assistantType="basic-medical-library" />}
            />

            {/* VisitStart pre-visit intake — full-bleed, like ChartMind and
                CareStart: StepperPageShell owns the height, padding and scroll. No
                location is passed — a session need not be tied to a clinic. */}
            <Route
              path="/visitstart"
              element={
                <React.Suspense fallback={<Loading page />}>
                  <VisitStartPage />
                </React.Suspense>
              }
            />

            <Route
              path="/assistant"
              element={
                <React.Suspense fallback={<Loading page />}>
                  <AssistantPage />
                </React.Suspense>
              }
            />

            {/* CareStart inside the dashboard shell (sidebar stays). The public, anonymous
                entry lives at the top-level /carestart route. */}
            <Route
              path="/carestart"
              element={
                <React.Suspense fallback={<Loading page />}>
                  <CareStartPage embedded publicPath="/dashboard/carestart" />
                </React.Suspense>
              }
            />

            <Route
              path="/settings"
              element={<Settings uid={user?.uid} subscription={subscription} />}
            />
            <Route path="/masterchart" element={<HealthRecordsHub />} />
            <Route path="/masterchart/setup" element={<MasterChartSetupPage />} />
            <Route path="/masterchart/demographics" element={<DemographicsPage />} />
            <Route path="/masterchart/conditions" element={<MedicalConditionsPage />} />
            <Route path="/masterchart/surgical-history" element={<SurgicalHistoryPage />} />
            <Route path="/masterchart/allergies" element={<AllergiesPage />} />
            <Route path="/masterchart/other-history" element={<OtherHistoryPage />} />
            <Route path="/masterchart/share" element={<HealthRecordsHub openShare />} />
            {/* Partner API demo: a hospital sends the patient their record. Sandbox only. */}
            {isDevEnvironment() && (
              <Route path="/masterchart/import" element={<HealthRecordsHub openImport />} />
            )}
            {/* Old MasterChart URLs (emails, QR codes, bookmarks) land on the same section. */}
            <Route path="/health-records" element={<MasterChartLegacyRedirect />} />
            <Route path="/health-records/*" element={<MasterChartLegacyRedirect />} />
            <Route path="/medications" element={<MedicationsPage />} />
            {/* TODO: Messages (facility <> patient comms) disabled for beta launch — re-enable when facility role ships */}
            {/* <Route path="/messages" element={<MessagesPage />} /> */}
            <Route path="/discharge-instructions" element={<DischargeInstructionsPage />} />
            {/* TODO: Medication tracking disabled until Twilio BAA is in place */}
            {/* <Route
              path="/medication-tracking"
              element={<MedicationTrackingPage />}
            /> */}

            {!userData?.dailyPassExpiresAt && (
              <Route path="/upgrade" element={<UpgradePage />} />
            )}

            {/* Global-admin-only routes */}
            <Route
              path="/admin/approval"
              element={
                isGlobalAdmin ? (
                  <ApprovalPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/admin/assistants"
              element={
                isGlobalAdmin ? (
                  <AssistantManagerPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/admin/usage"
              element={
                isGlobalAdmin ? (
                  <UsageMonitorPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/prompts"
              element={
                isGlobalAdmin ? (
                  <PromptsManagerPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />

            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </PromptsManagerProvider>
      </DashboardLayout>
    </VisitStartDraftProvider>
  );
};

export default PatientRoutes;
